import React from 'react';
import App from '../App';
import { ROUTES } from './routes';
import AdminLayoutWrapper from '../layouts/AdminLayoutWrapper';
import ErrorBoundary from '../components/ErrorBoundary';

// Import actual page components
import HomeScreen from '../pages/HomeScreen';
import BusDataFormWizard from '../components/BusDataFormWizard';
import LPATestRequestFormWizard from '../components/LPATestRequestFormWizard';
import VolunteerForm from '../components/VolunteerForm';
import VolunteerSuccess from '../pages/VolunteerSuccess';
import AdminLogin from '../pages/AdminLogin';
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';
import AdminDashboard from '../pages/AdminDashboard';
import RequireAdmin from '../pages/RequireAdmin';
import RequirePermission from '../components/RequirePermission';
import NewEventManagement from '../pages/EventManagement/NewEventManagement';
import PatientQueue from '../pages/PatientQueue';
import UserManagement from '../pages/UserManagement';
import ReportsAnalytics from '../pages/ReportsAnalytics';
import WaitTimesAnalytics from '../pages/Analytics/WaitTimes';
import HealthOutcomesAnalytics from '../pages/Analytics/HealthOutcomes';
import ImpactAnalytics from '../pages/Analytics/Impact';
import OperationsAnalytics from '../pages/Analytics/Operations';
import EventsAnalytics from '../pages/Analytics/Events';
import PopulationAnalytics from '../pages/Analytics/Population';
import VolunteersAnalytics from '../pages/Analytics/Volunteers';
import BookingAnalytics from '../pages/Analytics/Booking';
import ReportsAnalyticsPage from '../pages/Analytics/Reports';
import ScreeningData from '../pages/ScreeningData';
import ScreeningEntry from '../pages/ScreeningEntry';
import PatientEdit from '../pages/PatientEdit';
import PublicEventBooking from '../pages/PublicEventBooking';
import Settings from '../pages/Settings';
import AuditLogs from '../pages/AuditLogs';
import MemberRecordEditPage from '../pages/MemberRecordEdit';
import CaseManagement from '../pages/CaseManagement/CaseManagement';
import Inbox from '../pages/Inbox';
import VolunteerRegistrations from '../pages/VolunteerRegistrations';
import CallLogs from '../pages/CallLogs';
import Pulse from '../pages/Pulse';
import FormConfigPage from '../pages/FormConfig';
import PatientComplete from '../pages/PatientComplete';
import PatientResults from '../pages/PatientResults';
import DataImport from '../pages/DataImport';
import BackendLogs from '../pages/BackendLogs';
import OnDemandStorageUnits from '../pages/OnDemand/StorageUnits';
import OnDemandHCWs from '../pages/OnDemand/HCWs';
import OnDemandEvents from '../pages/OnDemand/Events';
import OnDemandPatients from '../pages/OnDemand/Patients';
import OnDemandPayouts from '../pages/OnDemand/Payouts';
import OnDemandOrders from '../pages/OnDemand/Orders';
import SurveysPage from '../pages/Surveys';
import SurveyBuilder from '../pages/Surveys/Builder';
import SurveyResponsesPage from '../pages/Surveys/Responses';
import EmailCampaignsPage from '../pages/EmailCampaigns';
import EmailCampaignBuilder from '../pages/EmailCampaigns/Builder';
import EmailCampaignAnalytics from '../pages/EmailCampaigns/Analytics';
import EmailTemplatesPage from '../pages/EmailCampaigns/Templates';
import EmailAutomationsPage from '../pages/EmailAutomations';
import EmailAutomationBuilder from '../pages/EmailAutomations/Builder';
import PublicSurveyPage from '../pages/PublicSurvey';
import InsuranceEnrollmentPage from '../pages/InsuranceEnrollment';
import InsuranceEnrollmentsPage from '../pages/InsuranceEnrollments';
import DeleteAccountPage from '../pages/DeleteAccount';

// This is just a placeholder component for pages that don't exist yet
const PlaceholderPage = ({ title }) => (
  <div style={{ padding: '2rem', textAlign: 'center' }}>
    <h1>{title} Page</h1>
    <p>This page is under construction.</p>
  </div>
);

// Use the actual components for each page
const LoginPage = () => <AdminLogin />;
const RegisterPage = () => <PlaceholderPage title="Register" />;
const ForgotPasswordPage = () => <ForgotPassword />;
const ResetPasswordPage = () => <ResetPassword />;
const ProfilePage = () => <PlaceholderPage title="Profile" />;
const SettingsPage = () => (
  <RequireAdmin>
    <Settings />
  </RequireAdmin>
);
const BusScreeningPage = () => <BusDataFormWizard />;
const LPATestPage = () => <LPATestRequestFormWizard />;
const VolunteerPage = () => <VolunteerForm />;
const VolunteerSuccessPage = () => <VolunteerSuccess />;
const PublicBookingPage = () => <PublicEventBooking />;
const HistoryPage = () => <PlaceholderPage title="Screening History" />;
const AdminDashboardPage = () => (
  <RequirePermission permission="dashboard">
    <AdminDashboard />
  </RequirePermission>
);
const AdminUsersPage = () => (
  <RequirePermission permission="user_management">
    <UserManagement />
  </RequirePermission>
);
const AdminReportsPage = () => (
  <RequirePermission permission="analytics">
    <ReportsAnalytics />
  </RequirePermission>
);
const AdminWaitTimesPage = () => (
  <RequirePermission permission="analytics">
    <WaitTimesAnalytics />
  </RequirePermission>
);
const AdminHealthOutcomesPage = () => (
  <RequirePermission permission="analytics">
    <HealthOutcomesAnalytics />
  </RequirePermission>
);
const AdminImpactPage = () => (
  <RequirePermission permission="analytics">
    <ImpactAnalytics />
  </RequirePermission>
);
const AdminOperationsPage = () => (
  <RequirePermission permission="analytics">
    <OperationsAnalytics />
  </RequirePermission>
);
const AdminEventsAnalyticsPage = () => (
  <RequirePermission permission="analytics">
    <EventsAnalytics />
  </RequirePermission>
);
const AdminPopulationPage = () => (
  <RequirePermission permission="analytics">
    <PopulationAnalytics />
  </RequirePermission>
);
const AdminVolunteersAnalyticsPage = () => (
  <RequirePermission permission="analytics">
    <VolunteersAnalytics />
  </RequirePermission>
);
const AdminBookingAnalyticsPage = () => (
  <RequirePermission permission="analytics">
    <BookingAnalytics />
  </RequirePermission>
);
const AdminReportsAnalyticsPage = () => (
  <RequirePermission permission="analytics">
    <ReportsAnalyticsPage />
  </RequirePermission>
);
const AdminEventsPage = () => (
  <RequirePermission permission="event_management">
    <NewEventManagement />
  </RequirePermission>
);
const AdminAllMembersPage = () => (
  <RequirePermission permission="screening_data">
    <ScreeningData />
  </RequirePermission>
);
const AdminPatientQueuePage = () => (
  <RequirePermission permission="patient_queue">
    <PatientQueue />
  </RequirePermission>
);
const AdminScreeningEntryPage = () => (
  <RequirePermission permission="screening_data">
    <ScreeningEntry />
  </RequirePermission>
);
const AdminPatientEditPage = () => (
  <RequirePermission permission="patient_queue">
    <PatientEdit />
  </RequirePermission>
);
const AdminMemberRecordEditPage = () => (
  <RequirePermission permission="screening_data">
    <MemberRecordEditPage />
  </RequirePermission>
);
const AdminAuditLogsPage = () => (
  <RequirePermission permission="audit_logs">
    <AuditLogs />
  </RequirePermission>
);
const AdminCaseManagementPage = () => (
  <RequirePermission permission="case_management">
    <CaseManagement />
  </RequirePermission>
);
const AdminInboxPage = () => (
  <RequirePermission permission="messaging">
    <Inbox />
  </RequirePermission>
);
const AdminVolunteerRegistrationsPage = () => (
  <RequirePermission permission="event_management">
    <VolunteerRegistrations />
  </RequirePermission>
);
const AdminCallLogsPage = () => (
  <RequirePermission permission="voice_calls">
    <CallLogs />
  </RequirePermission>
);
const AdminPulsePage = () => (
  <RequirePermission permission="analytics">
    <Pulse />
  </RequirePermission>
);
const AdminFormConfigPage = () => (
  <RequireAdmin>
    <FormConfigPage />
  </RequireAdmin>
);
const AdminDataImportPage = () => (
  <RequireAdmin>
    <DataImport />
  </RequireAdmin>
);
const AdminBackendLogsPage = () => (
  <RequireAdmin>
    <BackendLogs />
  </RequireAdmin>
);
const AdminOnDemandStorageUnitsPage = () => (
  <RequirePermission permission="ondemand_management">
    <OnDemandStorageUnits />
  </RequirePermission>
);
const AdminOnDemandHCWsPage = () => (
  <RequirePermission permission="ondemand_management">
    <OnDemandHCWs />
  </RequirePermission>
);
const AdminOnDemandEventsPage = () => (
  <RequirePermission permission="ondemand_management">
    <OnDemandEvents />
  </RequirePermission>
);
const AdminOnDemandPatientsPage = () => (
  <RequirePermission permission="ondemand_management">
    <OnDemandPatients />
  </RequirePermission>
);
const AdminOnDemandPayoutsPage = () => (
  <RequirePermission permission="ondemand_management">
    <OnDemandPayouts />
  </RequirePermission>
);
const AdminOnDemandOrdersPage = () => (
  <RequirePermission permission="ondemand_management">
    <OnDemandOrders />
  </RequirePermission>
);
const AdminInsuranceEnrollmentsPage = () => (
  <RequirePermission permission="insurance_enrollment">
    <InsuranceEnrollmentsPage />
  </RequirePermission>
);
const PatientCompletePage = () => <PatientComplete />;
const PatientResultsPage = () => <PatientResults />;
const NotFoundPage = () => <PlaceholderPage title="404 Not Found" />;
const ServerErrorPage = () => <PlaceholderPage title="500 Server Error" />;

const currentDomain = window.location.hostname;

export const ROUTER_CONFIG = (() => {
  switch (currentDomain) {
    case 'admin.bha.net':
    case 'care.blackheartassociation.org':
    case '18.144.27.173': // Add IP address for development/testing
    case 'localhost':
    case '127.0.0.1':
      // Admin domain routes only
      return [
        {
          path: '/',
          element: <App />,
          errorElement: <ErrorBoundary><div /></ErrorBoundary>,
          children: [
            // Admin authentication routes
            { index: true, element: <LoginPage /> },
            { path: ROUTES.LOGIN, element: <LoginPage /> },
            { path: ROUTES.REGISTER, element: <RegisterPage /> },
            { path: ROUTES.FORGOT_PASSWORD, element: <ForgotPasswordPage /> },
            { path: ROUTES.RESET_PASSWORD, element: <ResetPasswordPage /> },
            
            // Public routes (for testing)
            { path: '/home', element: <HomeScreen /> },
            { path: ROUTES.LPA_TEST, element: <LPATestPage /> },
            { path: ROUTES.VOLUNTEER, element: <VolunteerPage /> },
            { path: ROUTES.VOLUNTEER_SUCCESS, element: <VolunteerSuccessPage /> },
            { path: ROUTES.BUS_SCREENING, element: <BusScreeningPage /> },
            { path: ROUTES.PUBLIC_BOOKING, element: <PublicBookingPage /> },
            { path: ROUTES.PUBLIC_BOOKING_CONFIRMATION, element: <PublicBookingPage /> },
            { path: ROUTES.PUBLIC_EVENTS, element: <PublicBookingPage /> },
            { path: ROUTES.PATIENT_COMPLETE, element: <PatientCompletePage /> },
            { path: ROUTES.PATIENT_RESULTS, element: <PatientResultsPage /> },
            { path: ROUTES.PUBLIC_SURVEY, element: <PublicSurveyPage /> },
            { path: ROUTES.PUBLIC_INSURANCE, element: <InsuranceEnrollmentPage /> },
            { path: ROUTES.DELETE_ACCOUNT, element: <DeleteAccountPage /> },
            
            // Admin routes with persistent layout
            {
              element: <AdminLayoutWrapper />,
              children: [
                // Admin protected routes
                { path: ROUTES.DASHBOARD, element: <AdminDashboardPage /> },
                { path: ROUTES.PROFILE, element: <ProfilePage /> },
                { path: ROUTES.SETTINGS, element: <SettingsPage /> },
                { path: ROUTES.HISTORY, element: <HistoryPage /> },
                
                // Admin-specific routes
                { path: ROUTES.ADMIN_DASHBOARD, element: <AdminDashboardPage /> },
                { path: ROUTES.ADMIN_USERS, element: <AdminUsersPage /> },
                { path: ROUTES.ADMIN_REPORTS, element: <AdminReportsPage /> },
                { path: ROUTES.ADMIN_ANALYTICS, element: <AdminWaitTimesPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_WAIT_TIMES, element: <AdminWaitTimesPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_HEALTH_OUTCOMES, element: <AdminHealthOutcomesPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_IMPACT, element: <AdminImpactPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_OPERATIONS, element: <AdminOperationsPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_EVENTS, element: <AdminEventsAnalyticsPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_POPULATION, element: <AdminPopulationPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_VOLUNTEERS, element: <AdminVolunteersAnalyticsPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_BOOKING, element: <AdminBookingAnalyticsPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_REPORTS, element: <AdminReportsAnalyticsPage /> },
                { path: ROUTES.ADMIN_EVENTS, element: <AdminEventsPage /> },
                { path: ROUTES.ADMIN_ALL_MEMBERS, element: <AdminAllMembersPage /> },
                { path: ROUTES.ADMIN_PATIENT_QUEUE, element: <AdminPatientQueuePage /> },
                { path: ROUTES.ADMIN_SCREENING_ENTRY, element: <AdminScreeningEntryPage /> },
                { path: ROUTES.ADMIN_PATIENT_EDIT, element: <AdminPatientEditPage /> },
                { path: ROUTES.ADMIN_MEMBER_RECORD_EDIT, element: <AdminMemberRecordEditPage /> },
                { path: ROUTES.ADMIN_CASE_MANAGEMENT, element: <AdminCaseManagementPage /> },
                { path: ROUTES.ADMIN_INBOX, element: <AdminInboxPage /> },
                { path: ROUTES.ADMIN_AUDIT_LOGS, element: <AdminAuditLogsPage /> },
                { path: ROUTES.ADMIN_VOLUNTEER_REGISTRATIONS, element: <AdminVolunteerRegistrationsPage /> },
                { path: ROUTES.ADMIN_CALL_LOGS, element: <AdminCallLogsPage /> },
                { path: ROUTES.ADMIN_PULSE, element: <AdminPulsePage /> },
                { path: ROUTES.ADMIN_FORM_CONFIG, element: <AdminFormConfigPage /> },
                { path: ROUTES.ADMIN_DATA_IMPORT, element: <AdminDataImportPage /> },
                // On-Demand Healthcare
                { path: ROUTES.ADMIN_ONDEMAND_STORAGE, element: <AdminOnDemandStorageUnitsPage /> },
                { path: ROUTES.ADMIN_ONDEMAND_HCWS, element: <AdminOnDemandHCWsPage /> },
                { path: ROUTES.ADMIN_ONDEMAND_EVENTS, element: <AdminOnDemandEventsPage /> },
                { path: ROUTES.ADMIN_ONDEMAND_PATIENTS, element: <AdminOnDemandPatientsPage /> },
                { path: ROUTES.ADMIN_ONDEMAND_PAYOUTS, element: <AdminOnDemandPayoutsPage /> },
                { path: ROUTES.ADMIN_ONDEMAND_ORDERS, element: <AdminOnDemandOrdersPage /> },
                // Surveys
                { path: ROUTES.ADMIN_SURVEYS, element: <RequireAdmin><SurveysPage /></RequireAdmin> },
                { path: ROUTES.ADMIN_SURVEY_BUILDER_NEW, element: <RequireAdmin><SurveyBuilder /></RequireAdmin> },
                { path: ROUTES.ADMIN_SURVEY_BUILDER, element: <RequireAdmin><SurveyBuilder /></RequireAdmin> },
                { path: ROUTES.ADMIN_SURVEY_RESPONSES, element: <RequireAdmin><SurveyResponsesPage /></RequireAdmin> },
                { path: ROUTES.ADMIN_SURVEY_PREVIEW, element: <RequireAdmin><PublicSurveyPage previewMode /></RequireAdmin> },
                // Email Campaigns
                { path: ROUTES.ADMIN_EMAIL_CAMPAIGNS, element: <RequireAdmin><EmailCampaignsPage /></RequireAdmin> },
                { path: ROUTES.ADMIN_EMAIL_CAMPAIGN_BUILDER_NEW, element: <RequireAdmin><EmailCampaignBuilder /></RequireAdmin> },
                { path: ROUTES.ADMIN_EMAIL_CAMPAIGN_BUILDER, element: <RequireAdmin><EmailCampaignBuilder /></RequireAdmin> },
                { path: ROUTES.ADMIN_EMAIL_CAMPAIGN_ANALYTICS, element: <RequireAdmin><EmailCampaignAnalytics /></RequireAdmin> },
                { path: ROUTES.ADMIN_EMAIL_TEMPLATES, element: <RequireAdmin><EmailTemplatesPage /></RequireAdmin> },
                { path: ROUTES.ADMIN_EMAIL_AUTOMATIONS, element: <RequireAdmin><EmailAutomationsPage /></RequireAdmin> },
                { path: ROUTES.ADMIN_EMAIL_AUTOMATION_BUILDER_NEW, element: <RequireAdmin><EmailAutomationBuilder /></RequireAdmin> },
                { path: ROUTES.ADMIN_EMAIL_AUTOMATION_BUILDER, element: <RequireAdmin><EmailAutomationBuilder /></RequireAdmin> },
                { path: ROUTES.ADMIN_BACKEND_LOGS, element: <AdminBackendLogsPage /> },
                { path: ROUTES.ADMIN_INSURANCE_ENROLLMENTS, element: <AdminInsuranceEnrollmentsPage /> },
              ]
            },
            
            // Error routes
            { path: ROUTES.NOT_FOUND, element: <NotFoundPage /> },
            { path: ROUTES.SERVER_ERROR, element: <ServerErrorPage /> },
            
            // Catch-all route for 404
            { path: '*', element: <NotFoundPage /> }
          ]
        }
      ];
      
    default:
      // Public domain routes (bha.net and any other domains)
      return [
        {
          path: '/',
          element: <App />,
          errorElement: <ErrorBoundary><div /></ErrorBoundary>,
          children: [
            // Public routes
            { index: true, element: <HomeScreen /> },
            { path: ROUTES.LPA_TEST, element: <LPATestPage /> },
            { path: ROUTES.VOLUNTEER, element: <VolunteerPage /> },
            { path: ROUTES.VOLUNTEER_SUCCESS, element: <VolunteerSuccessPage /> },
            { path: ROUTES.BUS_SCREENING, element: <BusScreeningPage /> },
            { path: ROUTES.PUBLIC_BOOKING, element: <PublicBookingPage /> },
            { path: ROUTES.PUBLIC_BOOKING_CONFIRMATION, element: <PublicBookingPage /> },
            { path: ROUTES.PUBLIC_EVENTS, element: <PublicBookingPage /> },
            { path: ROUTES.PATIENT_COMPLETE, element: <PatientCompletePage /> },
            { path: ROUTES.PATIENT_RESULTS, element: <PatientResultsPage /> },
            { path: ROUTES.PUBLIC_SURVEY, element: <PublicSurveyPage /> },
            { path: ROUTES.PUBLIC_INSURANCE, element: <InsuranceEnrollmentPage /> },
            { path: ROUTES.DELETE_ACCOUNT, element: <DeleteAccountPage /> },
            
            // Admin authentication routes
            { path: ROUTES.LOGIN, element: <LoginPage /> },
            { path: ROUTES.REGISTER, element: <RegisterPage /> },
            { path: ROUTES.FORGOT_PASSWORD, element: <ForgotPasswordPage /> },
            { path: ROUTES.RESET_PASSWORD, element: <ResetPasswordPage /> },
            
            // Admin routes with persistent layout
            {
              element: <AdminLayoutWrapper />,
              children: [
                // Admin protected routes
                { path: ROUTES.DASHBOARD, element: <AdminDashboardPage /> },
                { path: ROUTES.PROFILE, element: <ProfilePage /> },
                { path: ROUTES.SETTINGS, element: <SettingsPage /> },
                { path: ROUTES.HISTORY, element: <HistoryPage /> },
                
                // Admin-specific routes
                { path: ROUTES.ADMIN_DASHBOARD, element: <AdminDashboardPage /> },
                { path: ROUTES.ADMIN_USERS, element: <AdminUsersPage /> },
                { path: ROUTES.ADMIN_REPORTS, element: <AdminReportsPage /> },
                { path: ROUTES.ADMIN_ANALYTICS, element: <AdminWaitTimesPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_WAIT_TIMES, element: <AdminWaitTimesPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_HEALTH_OUTCOMES, element: <AdminHealthOutcomesPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_IMPACT, element: <AdminImpactPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_OPERATIONS, element: <AdminOperationsPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_EVENTS, element: <AdminEventsAnalyticsPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_POPULATION, element: <AdminPopulationPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_VOLUNTEERS, element: <AdminVolunteersAnalyticsPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_BOOKING, element: <AdminBookingAnalyticsPage /> },
                { path: ROUTES.ADMIN_ANALYTICS_REPORTS, element: <AdminReportsAnalyticsPage /> },
                { path: ROUTES.ADMIN_EVENTS, element: <AdminEventsPage /> },
                { path: ROUTES.ADMIN_ALL_MEMBERS, element: <AdminAllMembersPage /> },
                { path: ROUTES.ADMIN_PATIENT_QUEUE, element: <AdminPatientQueuePage /> },
                { path: ROUTES.ADMIN_SCREENING_ENTRY, element: <AdminScreeningEntryPage /> },
                { path: ROUTES.ADMIN_PATIENT_EDIT, element: <AdminPatientEditPage /> },
                { path: ROUTES.ADMIN_MEMBER_RECORD_EDIT, element: <AdminMemberRecordEditPage /> },
                { path: ROUTES.ADMIN_CASE_MANAGEMENT, element: <AdminCaseManagementPage /> },
                { path: ROUTES.ADMIN_INBOX, element: <AdminInboxPage /> },
                { path: ROUTES.ADMIN_AUDIT_LOGS, element: <AdminAuditLogsPage /> },
                { path: ROUTES.ADMIN_VOLUNTEER_REGISTRATIONS, element: <AdminVolunteerRegistrationsPage /> },
                { path: ROUTES.ADMIN_CALL_LOGS, element: <AdminCallLogsPage /> },
                { path: ROUTES.ADMIN_PULSE, element: <AdminPulsePage /> },
                { path: ROUTES.ADMIN_FORM_CONFIG, element: <AdminFormConfigPage /> },
                { path: ROUTES.ADMIN_DATA_IMPORT, element: <AdminDataImportPage /> },
                // On-Demand Healthcare
                { path: ROUTES.ADMIN_ONDEMAND_STORAGE, element: <AdminOnDemandStorageUnitsPage /> },
                { path: ROUTES.ADMIN_ONDEMAND_HCWS, element: <AdminOnDemandHCWsPage /> },
                { path: ROUTES.ADMIN_ONDEMAND_EVENTS, element: <AdminOnDemandEventsPage /> },
                { path: ROUTES.ADMIN_ONDEMAND_PATIENTS, element: <AdminOnDemandPatientsPage /> },
                { path: ROUTES.ADMIN_ONDEMAND_PAYOUTS, element: <AdminOnDemandPayoutsPage /> },
                { path: ROUTES.ADMIN_ONDEMAND_ORDERS, element: <AdminOnDemandOrdersPage /> },
                // Surveys
                { path: ROUTES.ADMIN_SURVEYS, element: <RequireAdmin><SurveysPage /></RequireAdmin> },
                { path: ROUTES.ADMIN_SURVEY_BUILDER_NEW, element: <RequireAdmin><SurveyBuilder /></RequireAdmin> },
                { path: ROUTES.ADMIN_SURVEY_BUILDER, element: <RequireAdmin><SurveyBuilder /></RequireAdmin> },
                { path: ROUTES.ADMIN_SURVEY_RESPONSES, element: <RequireAdmin><SurveyResponsesPage /></RequireAdmin> },
                { path: ROUTES.ADMIN_SURVEY_PREVIEW, element: <RequireAdmin><PublicSurveyPage previewMode /></RequireAdmin> },
                // Email Campaigns
                { path: ROUTES.ADMIN_EMAIL_CAMPAIGNS, element: <RequireAdmin><EmailCampaignsPage /></RequireAdmin> },
                { path: ROUTES.ADMIN_EMAIL_CAMPAIGN_BUILDER_NEW, element: <RequireAdmin><EmailCampaignBuilder /></RequireAdmin> },
                { path: ROUTES.ADMIN_EMAIL_CAMPAIGN_BUILDER, element: <RequireAdmin><EmailCampaignBuilder /></RequireAdmin> },
                { path: ROUTES.ADMIN_EMAIL_CAMPAIGN_ANALYTICS, element: <RequireAdmin><EmailCampaignAnalytics /></RequireAdmin> },
                { path: ROUTES.ADMIN_EMAIL_TEMPLATES, element: <RequireAdmin><EmailTemplatesPage /></RequireAdmin> },
                { path: ROUTES.ADMIN_EMAIL_AUTOMATIONS, element: <RequireAdmin><EmailAutomationsPage /></RequireAdmin> },
                { path: ROUTES.ADMIN_EMAIL_AUTOMATION_BUILDER_NEW, element: <RequireAdmin><EmailAutomationBuilder /></RequireAdmin> },
                { path: ROUTES.ADMIN_EMAIL_AUTOMATION_BUILDER, element: <RequireAdmin><EmailAutomationBuilder /></RequireAdmin> },
                { path: ROUTES.ADMIN_BACKEND_LOGS, element: <AdminBackendLogsPage /> },
                { path: ROUTES.ADMIN_INSURANCE_ENROLLMENTS, element: <AdminInsuranceEnrollmentsPage /> },
              ]
            },
            
            // Error routes
            { path: ROUTES.NOT_FOUND, element: <NotFoundPage /> },
            { path: ROUTES.SERVER_ERROR, element: <ServerErrorPage /> },
            
            // Catch-all route for 404
            { path: '*', element: <NotFoundPage /> }
          ]
        }
      ];
  }
})();
